import React from 'react';
import { TimePicker } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';

export interface OASISTimeRenderProps {
  /** HH:mm string */
  value: string;
  onChange: (v: string) => void;
  label: string;
}

export interface OASISTimeProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  className?: string;
  style?: React.CSSProperties;
  hideLabel?: boolean;
  innerClassName?: string;
  labelClassName?: string;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  inputClassName?: string;
  children?: (props: OASISTimeRenderProps) => React.ReactNode;
}

export function OASISTime({
  qid,
  labelOverride,
  className,
  style,
  hideLabel,
  innerClassName,
  labelClassName,
  controlClassName,
  inputClassName,
  children,
}: OASISTimeProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);

  const rawAnswer = answers[qid];
  const value = rawAnswer != null && rawAnswer !== '' ? String(rawAnswer) : '';
  const onChange = (v: string) => onAnswerChange(qid, v);
  const handleTimeChange = (time: string | undefined) => {
    onAnswerChange(qid, time ?? '');
  };
  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;
  const resolvedControlClassName = resolveControlClassName(
    controlClassName,
    inputClassName
  );

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      {children ? (
        children({ value, onChange, label })
      ) : (
        <div className={innerClassName ?? BESPOKE_FIELD_STACK_GAP_CLASS}>
          {!hideLabel && (
            <label
              className={joinClasses(BESPOKE_FIELD_LABEL_CLASS, labelClassName)}
            >
              {label}
            </label>
          )}
          <TimePicker
            time={value || undefined}
            onTimeChange={handleTimeChange}
            disabled={inheritedDisabled}
            timePickerSize='default'
            className='w-full'
            inputClassName={resolvedControlClassName}
          />
        </div>
      )}
    </div>
  );
}

/** Vibecode authoring notes for `OASISTime` nodes (empty = omit from LLM bundle). */
export const OASIS_TIME_VIBECODE =
  '`OASISTime`: for schema `text` questions that store clock times (`HH:mm`)';
